import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import apis from '@src/apis';
import Card from '@src/components/Card';
import debounce from '@src/utils/debounce';
import TableFooterNew from '@src/components/NewTable/TableFooterNew';
import CreateApp from '@src/containers/CreateApp';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocation from '@src/hooks/useLocation';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { FEATURE } from '@src/constants/package';
import useUserInfo from '@src/hooks/useTtsUser';
import { StyledListAppsContainer } from './index.style';
import Header from './Header';
import Toolbar from './Toolbar';
import List from './List';
import UpgradeApiAccessPopup from './UpgradeApiAccessPopup';
import ApiTtsSttLaunchBanner from './ApiTtsSttLaunchBanner';

const PAGINATION_LIMIT = 5;
const INITIAL_PAGING = { page: 0, total: 0 };

const ListApps = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();

  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useLocation();
  const studioTtsApiEnabled = getFeatureValue(FEATURE_KEYS.STUDIO_TTS_API, {
    userId: user?.id,
    email: user?.email,
  });

  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    {
      userId: user?.id,
      email: user?.email,
      ip,
    },
  );

  const [paging, setPaging] = useState(INITIAL_PAGING);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT);
  const [openCreateAppDialog, setOpenCreateAppDialog] = useState(false);
  const [openPopupLockApiIntegration, setOpenPopupLockApiIntegration] =
    useState(false);

  const handleSearchInputChange = (e) => setSearch(e.target.value);

  const fetchApps = async () => {
    setLoading(true);
    const data = await apis.apps.getApps({
      limit: rowsPerPage,
      offset: paging.page * rowsPerPage,
      sort: 'createdAt_desc',
      search,
      searchFields: 'name',
      fields: 'id,name,active,expiresAt',
    });

    if (data?.status) {
      setApps(data.result.apps);
      setPaging({
        ...paging,
        total: data.result.metadata.total,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(fetchApps, 500)(search);
  }, [paging.page, search, rowsPerPage]);

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPaging({ ...paging, page: 0 });
  };

  const handleChangePage = (page) => {
    setPaging({ ...paging, page });
  };

  const handleOpenCreateAppDialog = () => {
    const { apiPackage = {} } = user || {};
    const { packageCode = '', packageExpiryDate = '' } = apiPackage;

    if (!studioTtsApiEnabled) {
      if (!packageCode) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'noApiPackage',
          }),
        );
        return;
      }
      const isExpiredPackage =
        packageExpiryDate && moment().isAfter(packageExpiryDate);
      if (isExpiredPackage) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'expiredPackage',
          }),
        );
        return;
      }
    }

    if (studioTtsApiEnabled) {
      const hasTtsApiFeature = ttsUser?.features?.includes(FEATURE.TTS_API);
      const isPackageExpired =
        user?.packageExpiryDate && moment().isAfter(user?.packageExpiryDate);
      if (!hasTtsApiFeature || isPackageExpired) {
        setOpenPopupLockApiIntegration(true);
        return;
      }
    }

    setOpenCreateAppDialog(true);
  };

  const handleCloseCreateAppDialog = () => {
    setOpenCreateAppDialog(false);
  };

  const handleClosePopupLockApiIntegration = () => {
    setOpenPopupLockApiIntegration(false);
  };

  return (
    <StyledListAppsContainer>
      <Card flexDirection="column" className="list-apps-card">
        <div className="list-apps-wrapper">
          <Header />
          {apiTtsSttLaunchEnabled && <ApiTtsSttLaunchBanner />}
          <Toolbar
            search={search}
            handleSearchInputChange={handleSearchInputChange}
            onCreateApp={handleOpenCreateAppDialog}
            apiTtsSttLaunchEnabled={apiTtsSttLaunchEnabled}
          />
          <List
            rowsPerPage={rowsPerPage}
            paging={paging}
            apps={apps}
            handleChangePage={handleChangePage}
            loading={loading}
          />
          <TableFooterNew
            total={paging.total}
            page={paging.page}
            rowsPerPage={rowsPerPage}
            onChangeRowPage={handleChangeRowsPerPage}
            onChangePage={handleChangePage}
            rowsPerPageOptions={[5, 25, 50]}
            styleFooter={{
              padding: '16px 16px 16px 0px',
              height: 'auto',
            }}
            styleSelect={{
              transform: 'translateX(5%) translateY(8%)',
            }}
          />
        </div>
      </Card>
      <CreateApp
        open={openCreateAppDialog}
        page={paging.page}
        onClose={handleCloseCreateAppDialog}
        onFetchApps={fetchApps}
        onSetPaging={setPaging}
      />
      {openPopupLockApiIntegration && (
        <UpgradeApiAccessPopup
          open={openPopupLockApiIntegration}
          onClose={handleClosePopupLockApiIntegration}
        />
      )}
    </StyledListAppsContainer>
  );
};

export default ListApps;
